export { stringify }

import { types } from './types.js'
import { isReactElement } from './utils/isReactElement.js'
import { isCallable } from './utils/isCallable.js'
import { isObject } from './utils/isObject.js'
import { addPathToReplacer, type Iterable, type Path } from './utils/addPathToReplacer.js'

type Replacer = (
  this: Iterable,
  key: string,
  value: unknown,
  serializer: (value: unknown) => string,
) => { replacement: unknown; resolved?: boolean } | undefined

function stringify(
  value: unknown,
  {
    forbidReactElements,
    space,
    valueName,
    sortObjectKeys,
    replacer: replacerUserProvided,
    htmlScriptSafe = true,
  }: {
    forbidReactElements?: boolean
    space?: number
    valueName?: string
    sortObjectKeys?: boolean
    replacer?: Replacer
    htmlScriptSafe?:
      | boolean
      | {
          escapeScripts?: boolean
          escapeURLs?: boolean
        }
  } = {},
): string {
  const serializer = (val: unknown) => JSON.stringify(val, addPathToReplacer(replacer), space)

  let serialized = serializer(value)
  if (htmlScriptSafe) {
    if (htmlScriptSafe === true || htmlScriptSafe.escapeScripts !== false) {
      // split/join instead of replaceAll: repo's TS lib target predates String.prototype.replaceAll (es2021)
      serialized = serialized.split('<').join('\\u003c')
    }
    if (htmlScriptSafe === true || htmlScriptSafe.escapeURLs !== false) {
      serialized = serialized.split('/').join('\\/')
    }
  }

  return serialized

  function replacer(this: Iterable, key: string, _valueAfterNativeJsonStringify: unknown, path: Path) {
    const valueOriginal = this[key]
    let value = valueOriginal

    {
      const ret = replacerUserProvided?.call(this, key, valueOriginal, serializer)
      if (ret) {
        value = ret.replacement
        if (ret.resolved !== false) return value
      }
    }

    if (forbidReactElements && isReactElement(value)) {
      throw genErr({
        value,
        valueType: 'React element',
        path,
        rootValueName: valueName,
      })
    }

    if (isCallable(value)) {
      const functionName = value.name
      throw genErr({
        value,
        valueType: 'function',
        path,
        rootValueName: valueName,
        problematicValueName: path.length === 0 ? functionName : undefined,
      })
    }

    for (const { is, serialize } of types.slice().reverse()) {
      if (is(value) as any) {
        //@ts-ignore
        return serialize(value, serializer)
      }
    }

    if (sortObjectKeys && isObject(value)) {
      const copy: Record<string, unknown> = {}
      Object.keys(value)
        .sort()
        .forEach((key) => {
          copy[key] = (value as Record<string, unknown>)[key]
        })
      value = copy
    }

    return value
  }
}

function genErr({
  value,
  valueType,
  path,
  rootValueName,
  problematicValueName,
}: {
  value: unknown
  valueType: ValueType
  path: Path
  rootValueName?: string
  problematicValueName?: string
}) {
  const subjectName = getSubjectName({ path, rootValueName, problematicValueName })
  const messageCore = `cannot serialize ${subjectName} because it's a ${valueType}` as const
  const err = new Error(`[@brillout/json-serializer](https://github.com/brillout/json-serializer) ${messageCore}.`)
  const pathString = getPathString(path, true)
  const errAddendum: ErrAddendum & { [stamp]: true } = {
    [stamp]: true,
    messageCore,
    value,
    path,
    pathString,
    subjectName,
  }
  Object.assign(err, errAddendum)
  return err
}

type ErrAddendum = {
  messageCore:
    | `cannot serialize ${string} because it's a function`
    | `cannot serialize ${string} because it's a React element`
  value: unknown
  path: Path
  pathString: string
  subjectName: string
}

const stamp = '_isJsonSerializerError'

type ValueType = 'React element' | 'function'

function getSubjectName({
  path,
  rootValueName,
  problematicValueName,
}: {
  path: Path
  rootValueName?: string
  problematicValueName?: string
}) {
  const pathString = getPathString(path, !rootValueName)
  let subjectName: string
  if (!pathString) {
    subjectName = rootValueName || problematicValueName || 'value'
  } else {
    if (problematicValueName) {
      subjectName = problematicValueName + ' at '
    } else {
      subjectName = ''
    }
    subjectName = subjectName + (rootValueName || '') + pathString
  }
  return subjectName
}

function getPathString(path: Path, canBeFirstKey: boolean): string {
  const pathString = path
    .map((key, i) => {
      if (typeof key === 'number') {
        return `[${key}]`
      }
      if (i === 0 && canBeFirstKey && isKeyDotNotationCompatible(key)) {
        return key
      }
      return getPropAccessNotation(key)
    })
    .join('')
  return pathString
}

function getPropAccessNotation(key: unknown): `.${string}` | `[${string}]` {
  return typeof key === 'string' && isKeyDotNotationCompatible(key) ? `.${key}` : `[${JSON.stringify(key)}]`
}

function isKeyDotNotationCompatible(key: string): boolean {
  return /^[a-z0-9\$_]+$/i.test(key)
}
